// Crystal lattice generation for Atomic Mode.
//
// Instead of placing atoms on a generic cubic grid, a material with a known
// crystal structure is built on its *real* lattice (fcc copper, bcc iron,
// diamond-cubic silicon, rock-salt NaCl, zincblende GaN/SiC, hexagonal
// graphene…). This makes the geometry physically meaningful: nearest-neighbour
// distances, coordination numbers and density come out right, so elastic
// behaviour, melting and diffusion reflect the actual material.
//
// A structure is defined by its conventional cell: lattice vectors (here
// orthorhombic/cubic or a hexagonal cell) and a basis of fractional positions,
// each tagged with which composition element (by index) sits there. The
// generator tiles the cell to fill a target box.

export type CrystalStructure =
  | "fcc"
  | "bcc"
  | "hcp"
  | "diamond"
  | "rocksalt"
  | "zincblende"
  | "wurtzite"
  | "graphene";

/** A basis site: fractional cell coords + which element (index into elements). */
interface BasisSite {
  f: [number, number, number];
  /** Index into the material's element list (0 = first element, …). */
  element: number;
}

interface CellDef {
  /** Conventional cell edge vectors (Å), columns a1,a2,a3. */
  vectors: [
    [number, number, number],
    [number, number, number],
    [number, number, number],
  ];
  basis: BasisSite[];
  /** Number of distinct elements the basis references. */
  elementCount: number;
}

const SQRT3 = Math.sqrt(3);

/**
 * Build the conventional cell for a structure at lattice constant(s).
 * `a` is the cubic/hexagonal a-constant in Å; `c` the hexagonal c (defaults to
 * an ideal ratio per structure).
 */
export function cellFor(
  structure: CrystalStructure,
  a: number,
  c?: number,
): CellDef {
  switch (structure) {
    case "fcc":
      return {
        vectors: [
          [a, 0, 0],
          [0, a, 0],
          [0, 0, a],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [0.5, 0.5, 0], element: 0 },
          { f: [0.5, 0, 0.5], element: 0 },
          { f: [0, 0.5, 0.5], element: 0 },
        ],
        elementCount: 1,
      };
    case "bcc":
      return {
        vectors: [
          [a, 0, 0],
          [0, a, 0],
          [0, 0, a],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [0.5, 0.5, 0.5], element: 0 },
        ],
        elementCount: 1,
      };
    case "diamond":
      return {
        vectors: [
          [a, 0, 0],
          [0, a, 0],
          [0, 0, a],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [0.5, 0.5, 0], element: 0 },
          { f: [0.5, 0, 0.5], element: 0 },
          { f: [0, 0.5, 0.5], element: 0 },
          { f: [0.25, 0.25, 0.25], element: 0 },
          { f: [0.75, 0.75, 0.25], element: 0 },
          { f: [0.75, 0.25, 0.75], element: 0 },
          { f: [0.25, 0.75, 0.75], element: 0 },
        ],
        elementCount: 1,
      };
    case "rocksalt":
      // NaCl: two interpenetrating fcc sublattices (element 0 and element 1).
      return {
        vectors: [
          [a, 0, 0],
          [0, a, 0],
          [0, 0, a],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [0.5, 0.5, 0], element: 0 },
          { f: [0.5, 0, 0.5], element: 0 },
          { f: [0, 0.5, 0.5], element: 0 },
          { f: [0.5, 0, 0], element: 1 },
          { f: [0, 0.5, 0], element: 1 },
          { f: [0, 0, 0.5], element: 1 },
          { f: [0.5, 0.5, 0.5], element: 1 },
        ],
        elementCount: 2,
      };
    case "zincblende":
      // GaAs/SiC/cubic-BN: fcc of element 0 + fcc of element 1 offset by ¼,¼,¼.
      return {
        vectors: [
          [a, 0, 0],
          [0, a, 0],
          [0, 0, a],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [0.5, 0.5, 0], element: 0 },
          { f: [0.5, 0, 0.5], element: 0 },
          { f: [0, 0.5, 0.5], element: 0 },
          { f: [0.25, 0.25, 0.25], element: 1 },
          { f: [0.75, 0.75, 0.25], element: 1 },
          { f: [0.75, 0.25, 0.75], element: 1 },
          { f: [0.25, 0.75, 0.75], element: 1 },
        ],
        elementCount: 2,
      };
    case "wurtzite": {
      // GaN/ZnO: hexagonal, ideal c/a = sqrt(8/3) ≈ 1.633.
      const cc = c ?? a * Math.sqrt(8 / 3);
      const u = 3 / 8;
      return {
        vectors: [
          [a, 0, 0],
          [-a / 2, (a * SQRT3) / 2, 0],
          [0, 0, cc],
        ],
        basis: [
          { f: [1 / 3, 2 / 3, 0], element: 0 },
          { f: [2 / 3, 1 / 3, 0.5], element: 0 },
          { f: [1 / 3, 2 / 3, u], element: 1 },
          { f: [2 / 3, 1 / 3, 0.5 + u], element: 1 },
        ],
        elementCount: 2,
      };
    }
    case "hcp": {
      const cc = c ?? a * Math.sqrt(8 / 3);
      return {
        vectors: [
          [a, 0, 0],
          [-a / 2, (a * SQRT3) / 2, 0],
          [0, 0, cc],
        ],
        basis: [
          { f: [1 / 3, 2 / 3, 0.25], element: 0 },
          { f: [2 / 3, 1 / 3, 0.75], element: 0 },
        ],
        elementCount: 1,
      };
    }
    case "graphene": {
      // Honeycomb sheet (also the in-plane cell of graphite / h-BN). a is the
      // lattice constant (~2.46 Å graphene, ~2.50 Å h-BN). Thin c so it's a 2D
      // sheet in 3D. The two sublattice sites are tagged element 0 and 1: for a
      // single-element material (graphene) the converter wraps the index, so
      // both become the one element; for h-BN they become B and N.
      const cc = c ?? 3.35;
      return {
        vectors: [
          [a, 0, 0],
          [-a / 2, (a * SQRT3) / 2, 0],
          [0, 0, cc],
        ],
        basis: [
          { f: [0, 0, 0], element: 0 },
          { f: [1 / 3, 1 / 3, 0], element: 1 },
        ],
        elementCount: 2,
      };
    }
  }
}

export interface LatticeAtom {
  /** Index into the material's element list. */
  element: number;
  x: number;
  y: number;
  z: number;
}

export interface LatticeFill {
  atoms: LatticeAtom[];
  /**
   * Periodic box that tiles seamlessly: an integer number of conventional
   * cells along each cell vector. Atoms lie in [0, box) so they don't overlap
   * their periodic images. For non-orthogonal cells (hexagonal) this is the
   * orthogonal bounding box of the replicated cells along x/y/z, which is a
   * valid periodic box because the in-plane vectors here close on it.
   */
  box: { x: number; y: number; z: number };
}

/** Number of atoms in one conventional cell of a structure. */
export function cellAtomCount(structure: CrystalStructure): number {
  return cellFor(structure, 1).basis.length;
}

/**
 * Tile `nx × ny × nz` whole conventional cells of a structure, returning the
 * atoms and the exact periodic box. Use this to hit an atom budget: choose the
 * cell counts so `nx·ny·nz·cellAtomCount` is near the target.
 */
export function fillLatticeCells(
  structure: CrystalStructure,
  a: number,
  nx: number,
  ny: number,
  nz: number,
  c?: number,
): LatticeFill {
  const cell = cellFor(structure, a, c);
  const [a1, a2, a3] = cell.vectors;
  const periodX = Math.abs(a1[0]) || a;
  const periodY = Math.abs(a2[1]) || a;
  const periodZ = Math.abs(a3[2]) || a;
  const atoms: LatticeAtom[] = [];
  for (let i = 0; i < nx; i++) {
    for (let j = 0; j < ny; j++) {
      for (let k = 0; k < nz; k++) {
        for (const site of cell.basis) {
          const fx = i + site.f[0];
          const fy = j + site.f[1];
          const fz = k + site.f[2];
          atoms.push({
            element: site.element,
            x: fx * a1[0] + fy * a2[0] + fz * a3[0],
            y: fx * a1[1] + fy * a2[1] + fz * a3[1],
            z: fx * a1[2] + fy * a2[2] + fz * a3[2],
          });
        }
      }
    }
  }
  return { atoms, box: { x: nx * periodX, y: ny * periodY, z: nz * periodZ } };
}

/**
 * Tile a structure's conventional cell to fill *at least* the requested box,
 * returning atoms plus the EXACT periodic box (a whole number of cells). Using
 * whole cells is essential: a box that cut through a cell would make edge
 * atoms overlap their periodic images and blow the simulation up.
 *
 * For cubic structures the box is (nx·a, ny·a, nz·c). For hexagonal cells the
 * a1/a2 vectors span a rhombus; we replicate whole cells and report the
 * orthogonal period (nx·a in x, ny·(a·√3/2)… ) — adequate for our slab use
 * since runs are thermostatted, not strain-measured, but documented as such.
 */
export function fillLattice(
  structure: CrystalStructure,
  a: number,
  box: { x: number; y: number; z: number },
  c?: number,
): LatticeFill {
  const cell = cellFor(structure, a, c);
  const [a1, a2, a3] = cell.vectors;
  const periodX = Math.abs(a1[0]) || a;
  const periodY = Math.abs(a2[1]) || a;
  const periodZ = Math.abs(a3[2]) || a;
  const nx = Math.max(1, Math.round(box.x / periodX));
  const ny = Math.max(1, Math.round(box.y / periodY));
  const nz = Math.max(1, Math.round(box.z / periodZ));
  return fillLatticeCells(structure, a, nx, ny, nz, c);
}
